import * as React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import { SectionCard, currency } from './ui';
import { TRUE_COLORS } from '../theme';
import useAxios from '../utils/useAxios';
import { moduleOn } from '../utils/features';
import { ShellContext } from './layout/AppShell';
import type { BaseProduct } from './BaseCatalog';
import { Category, Supplier } from '../types';

export type ProductDraft = {
  name: string;
  barcode: string;
  sku: string;
  description: string;
  price: string;
  cost: string;
  display: boolean;
  online_visible: boolean;
  sold_out: boolean;
  /** Announced but not sold yet: "Próximamente" online, not at the registers. */
  coming_soon: boolean;
  category: Category[];
  suppliers: Supplier[];
  /** Option groups (api/modifier-groups/) the product offers, in order. */
  modifier_groups: number[];
};

export type ModifierGroupRef = { id: number; name: string; min_select: number; max_select: number | null };

export const emptyDraft: ProductDraft = {
  name: '', barcode: '', sku: '', description: '', price: '', cost: '', display: true, online_visible: true, sold_out: false, coming_soon: false, category: [], suppliers: [], modifier_groups: [],
};

export const draftFromProduct = (p: any): ProductDraft => ({
  name: p.name ?? '',
  barcode: p.barcode ?? '',
  sku: p.sku ?? '',
  description: p.description ?? '',
  price: p.price != null ? String(p.price) : '',
  cost: p.cost != null ? String(p.cost) : '',
  display: p.display ?? true,
  online_visible: p.online_visible ?? true,
  sold_out: p.sold_out ?? false,
  coming_soon: p.coming_soon ?? false,
  category: p.category ?? [],
  suppliers: p.suppliers ?? [],
  modifier_groups: p.modifier_groups ?? [],
});

/** Payload accepted by POST/PUT api/products/ (categories & suppliers as id lists). */
export const draftToPayload = (d: ProductDraft) => ({
  name: d.name.trim(),
  barcode: d.barcode.trim(),
  sku: d.sku.trim() || null,
  description: d.description.trim(),
  price: Number(d.price) || 0,
  cost: Number(d.cost) || 0,
  display: d.display,
  online_visible: d.online_visible,
  sold_out: d.sold_out,
  coming_soon: d.coming_soon,
  category: d.category.map(c => c.id),
  suppliers: d.suppliers.map(s => s.id),
  modifier_groups: d.modifier_groups,
});

/** Turns DRF field errors into a readable Spanish message. */
export const productErrorMessage = (err: any) => {
  const data = err?.response?.data;
  if (!data || typeof data !== 'object') return 'No se pudo guardar el producto.';
  const labels: Record<string, string> = { name: 'Nombre', barcode: 'Código de barras', sku: 'SKU', price: 'Precio', cost: 'Costo' };
  const translate = (msg: string) => msg
    .replace('Barcode ya existe.', 'ya está registrado en otro producto.')
    .replace('Barcode cannot be changed.', 'no se puede cambiar.')
    .replace('This field may not be blank.', 'es obligatorio.')
    .replace('This field is required.', 'es obligatorio.');
  return Object.entries(data)
    .map(([key, value]) => `${labels[key] ?? ''} ${translate([value].flat().join(' '))}`.trim())
    .join(' · ');
};

const AvailabilitySwitch = ({ checked, onChange, label, hint }: { checked: boolean; onChange: (value: boolean) => void; label: string; hint: string }) => (
  <FormControlLabel
    control={<Switch checked={checked} onChange={e => onChange(e.target.checked)} />}
    label={
      <Box>
        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{hint}</Typography>
      </Box>
    }
    sx={{ alignItems: 'flex-start', m: 0, '& .MuiSwitch-root': { mt: -0.5 } }}
  />
);

type Props = {
  draft: ProductDraft;
  onChange: (d: ProductDraft) => void;
  categories: Category[];
  suppliers: Supplier[];
  error?: string;
  /** Barcode is immutable once created. */
  lockBarcode?: boolean;
  /** Extra section, e.g. initial stock on create. */
  extra?: React.ReactNode;
  /** Photos and videos: a picker on create, the live manager on edit. */
  media?: React.ReactNode;
  /** The business's option groups; the "Opciones al vender" section shows when given. */
  modifierGroups?: ModifierGroupRef[];
};

/** A new product's barcode looked up in the base catalog (Receipt-Web-Server api/base_catalog.py): its usual name. */
const useBaseSuggestion = (barcode: string, enabled: boolean) => {
  const axios = useAxios();
  const [found, setFound] = React.useState<BaseProduct | null>(null);
  React.useEffect(() => {
    setFound(null);
    const code = barcode.trim();
    if (!enabled || !/^\d{8,14}$/.test(code)) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      axios.get(`api/base-catalog/?barcode=${code}`).then(r => { if (alive) setFound(r.data); }).catch(() => {});
    }, 400);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [axios, barcode, enabled]);
  return found;
};

const ProductForm = ({ draft, onChange, categories, suppliers, error, lockBarcode, extra, media, modifierGroups }: Props) => {
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => onChange({ ...draft, [key]: value });
  const suggestion = useBaseSuggestion(draft.barcode, !lockBarcode);
  // Suppliers and options show for businesses that use them (Configuración › Tu negocio), or when this product has some.
  const { features } = React.useContext(ShellContext);
  const showSuppliers = moduleOn(features, 'inventory') || draft.suppliers.length > 0;
  const showOptions = !!modifierGroups && (moduleOn(features, 'options') || draft.modifier_groups.length > 0);
  const price = Number(draft.price) || 0;
  const cost = Number(draft.cost) || 0;
  const margin = price > 0 ? (price - cost) / price : null;

  return (
    <Grid container spacing={3}>
      {error && (
        <Grid size={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      <Grid size={{ xs: 12, lg: 8 }}>
        <Stack spacing={3}>
          <SectionCard title="Información básica" subtitle="Así aparecerá el producto en TRUE POS y en tus reportes.">
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField label="Nombre" required fullWidth autoFocus value={draft.name} onChange={e => set('name', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Código de barras"
                  fullWidth
                  value={draft.barcode}
                  disabled={lockBarcode}
                  helperText={lockBarcode ? 'El código de barras no se puede cambiar.' : 'Escanéalo o escríbelo. Si el producto no tiene (un platillo, un servicio), déjalo vacío y se crea uno.'}
                  onChange={e => set('barcode', e.target.value)}
                />
              </Grid>
              {suggestion && (
                <Grid size={12}>
                  <Alert severity={suggestion.in_catalog ? 'warning' : 'info'} data-testid="base-suggestion"
                    action={!suggestion.in_catalog && draft.name.trim() !== suggestion.name
                      ? <Button color="inherit" size="small" onClick={() => set('name', suggestion.name)}>Usar este nombre</Button> : undefined}>
                    {suggestion.in_catalog
                      ? `Ya tienes este código en tu catálogo (${currency(suggestion.price)}).`
                      : <>Es <strong>{suggestion.name}</strong> en el catálogo base. <Typography component="span" variant="caption">Datos: Open Food Facts</Typography></>}
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="SKU" fullWidth value={draft.sku} helperText="Opcional" onChange={e => set('sku', e.target.value)} />
              </Grid>
              <Grid size={12}>
                <TextField label="Descripción" fullWidth multiline minRows={2} value={draft.description} onChange={e => set('description', e.target.value)} />
              </Grid>
            </Grid>
          </SectionCard>

          {media && (
            <SectionCard title="Fotos y videos" subtitle="Se muestran en tu catálogo en línea y en pedidos en línea.">
              {media}
            </SectionCard>
          )}

          <SectionCard title="Organización" subtitle="Agrupa tus productos para filtrarlos y analizarlos.">
            <Stack spacing={2}>
              <Autocomplete
                multiple
                options={categories}
                value={draft.category}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => set('category', value)}
                renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
                renderInput={params => <TextField {...params} label="Categorías" placeholder={draft.category.length ? '' : 'Selecciona categorías'} />}
                noOptionsText="Crea categorías desde Productos → Categorías"
              />
              {showSuppliers && <Autocomplete
                multiple
                options={suppliers}
                value={draft.suppliers}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => set('suppliers', value)}
                renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
                renderInput={params => <TextField {...params} label="Proveedores" placeholder={draft.suppliers.length ? '' : 'Selecciona proveedores'} />}
                noOptionsText="Agrega proveedores desde Inventario → Proveedores"
              />}
            </Stack>
          </SectionCard>

          {showOptions && modifierGroups && (
            <SectionCard
              title="Opciones al vender"
              subtitle="Variantes y complementos que el cajero elige al vender. También aparecen en tu catálogo en línea."
              action={<Button size="small" component={RouterLink} to="/admin/modifiers">Administrar</Button>}
            >
              <Autocomplete
                multiple
                options={modifierGroups}
                value={draft.modifier_groups.map(id => modifierGroups.find(g => g.id === id)).filter(Boolean) as ModifierGroupRef[]}
                getOptionLabel={g => g.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, value) => set('modifier_groups', value.map(g => g.id))}
                renderTags={(value, getTagProps) => value.map((g, i) => <Chip size="small" label={g.name} {...getTagProps({ index: i })} key={g.id} />)}
                renderInput={params => <TextField {...params} label="Grupos de opciones" placeholder={draft.modifier_groups.length ? '' : 'Elige grupos'}
                  helperText="Se preguntan en este orden." />}
                noOptionsText="Crea grupos en Productos → Opciones"
              />
            </SectionCard>
          )}

          {extra}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, lg: 4 }}>
        <Stack spacing={3} sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
          <SectionCard title="Precio">
            <Stack spacing={2}>
              <TextField
                label="Precio de venta"
                type="number"
                required
                fullWidth
                value={draft.price}
                onChange={e => set('price', e.target.value)}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
              />
              <TextField
                label="Costo"
                type="number"
                fullWidth
                value={draft.cost}
                onChange={e => set('cost', e.target.value)}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
              />
              <Box sx={{ bgcolor: TRUE_COLORS.lightBg, borderRadius: 2, p: 2 }}>
                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Ganancia por unidad</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{currency(price - cost)}</Typography>
                </Stack>
                <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Margen</Typography>
                  <Typography sx={{
                    fontWeight: 700, fontSize: '0.875rem',
                    color: margin == null ? 'text.secondary' : margin < 0 ? TRUE_COLORS.danger : TRUE_COLORS.success,
                  }}>
                    {margin == null ? '—' : `${(margin * 100).toFixed(1)}%`}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="Disponibilidad">
            <Stack spacing={2}>
              <AvailabilitySwitch
                checked={draft.display}
                onChange={value => set('display', value)}
                label="Visible en punto de venta"
                hint="Los cajeros podrán buscarlo y venderlo en TRUE POS."
              />
              <AvailabilitySwitch
                checked={draft.online_visible}
                onChange={value => set('online_visible', value)}
                label="Mostrar en el catálogo en línea"
                hint="Aparece en tu página receipt.com.mx/<tu-negocio> con sus fotos y videos."
              />
              <AvailabilitySwitch
                checked={draft.sold_out}
                onChange={value => set('sold_out', value)}
                label="Agotado"
                hint="El catálogo en línea lo muestra como agotado mientras esté activado."
              />
              <AvailabilitySwitch
                checked={draft.coming_soon}
                onChange={value => set('coming_soon', value)}
                label="Próximamente"
                hint="Se anuncia en el catálogo en línea, pero aún no se vende en TRUE POS ni se puede pedir."
              />
            </Stack>
          </SectionCard>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ProductForm;
